<template>
  <div class="news-app">
    <Preloader :backgroundColor="preloaderColor"></Preloader>

    <!-- Header -->
    <HeaderNews
      subtitle="Noticias y contenido sobre Inteligencia Artificial"
      :showSearch="true"
      :searchQuery.sync="query"
      :showMobileMenu="true"
      @toggle-mobile-menu="toggleMobileMenu"
    />

    <!-- Main Layout -->
    <div class="news-layout">
      <!-- Sidebar -->
      <Sidebar :class="{'sidebar--mobile-visible': showMobileSidebar}" @navigate="showMobileSidebar = false" @toggle-collapse="handleSidebarCollapse" />

      <!-- Mobile Overlay -->
      <div
        v-if="showMobileSidebar"
        class="mobile-overlay"
        @click="toggleMobileMenu"
      ></div>

      <!-- Main Content -->
      <div class="news-body">
      <main class="news-main" ref="newsMain">
        <div class="news-main__container">
        <!-- Header de la sección -->
        <div class="section-header">
          <router-link to="/news" class="back-btn" aria-label="Volver a noticias" title="Volver a noticias">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
          </router-link>
          <div class="company-avatar">
            <img :src="currentCompanyData.image" :alt="currentCompanyData.name" />
          </div>
          <div class="section-title-content">
            <h1 class="section-title">{{ currentCompanyData.name }}</h1>
            <p class="section-description">{{ getCompanyCount(currentCompanyData.category) }} noticias</p>
          </div>
          <p v-if="currentCompanyData.description" class="company-about">{{ currentCompanyData.description }}</p>
        </div>

        <!-- Content scrollable -->
        <section class="news-content" ref="scrollContainer">
          <!-- Error Message -->
          <div v-if="error" class="error-message">
            <div class="error-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <div class="error-content">
              <h4>Error al cargar noticias</h4>
              <p>{{ error }}</p>
            </div>
          </div>

          <!-- Feed List -->
          <FeedList :items="filteredItems" :loading="initialLoading">
            <!-- Destacado de la empresa (ocupa 2 cards), configurado en config/companyHighlights.config.js -->
            <template #pinned>
              <article v-if="highlight && !highlightFailed" class="company-hero">
                <div class="company-hero__media">
                  <video v-if="highlight.type === 'video' && !mediaFailed" :src="highlight.src"
                         :poster="highlight.poster" autoplay muted loop playsinline
                         preload="metadata" @error="onHighlightError"></video>
                  <img v-else :src="mediaFailed ? highlight.poster : highlight.src" :alt="highlight.title || ''" @error="onHighlightError">
                </div>
                <component v-if="highlight.title" :is="highlight.link ? 'a' : 'div'" class="company-hero__caption"
                           :href="highlight.link" target="_blank" rel="noopener">
                  <span class="company-hero__label">Destacado</span>
                  <span class="company-hero__title">{{ highlight.title }}</span>
                </component>
              </article>
            </template>
          </FeedList>

          <!-- Loading más items -->
          <div v-if="loadingMore && !initialLoading" class="loading-more">
            <div class="loading-spinner"></div>
            <span>Cargando más contenido...</span>
          </div>

          <!-- Botón Cargar Más -->
          <div v-if="!loadingMore && !initialLoading && currentChunk < totalChunks && visible.length > 0" class="load-more-container">
            <button @click="loadChunk" class="load-more-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              <span>Cargar más contenido ({{ currentChunk }}/{{ totalChunks }})</span>
            </button>
          </div>

          <!-- Fin de resultados -->
          <div v-if="!loadingMore && currentChunk >= totalChunks && visible.length > 0" class="end-message">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>Has visto todo el contenido disponible</span>
          </div>
        </section>
        </div>
      </main>

      <!-- Footer -->
      <Footer />
      </div>
    </div>

    <!-- Botón scroll to top -->
    <button
      @click="scrollToTop"
      class="scroll-to-top"
      :class="{'scroll-to-top--visible': showScrollTop}"
      aria-label="Volver al inicio"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="18 15 12 9 6 15"/>
      </svg>
    </button>
  </div>
</template>

<script>
import HeaderNews from '../components/HeaderNews';
import Sidebar from '../components/Sidebar';
import FeedList from '../components/FeedList';
import Footer from '../components/Footer';
import Preloader from '../../../components/Preloader';
import feedChunksConfig from '../../../config/feedChunks.config';
import companyHighlights from '../../../config/companyHighlights.config';
import topCompanies from '../../../config/topCompanies.config';

export default {
  name: 'TopCompanies',
  components: {
    HeaderNews,
    Sidebar,
    FeedList,
    Footer,
    Preloader
  },
  data() {
    return {
      // Datos de chunks
      loadedItems: [],           // Todos los items cargados
      currentChunk: 0,            // Chunk actual
      totalChunks: 0,             // Total de chunks disponibles

      // Estados de carga
      initialLoading: false,      // Primera carga
      loadingMore: false,         // Cargando más chunks
      error: '',

      // Filtros
      query: '',
      metadata: null,             // Metadata completa del feed

      // Scroll to top
      showScrollTop: false,

      // Responsive
      windowWidth: window.innerWidth,

      // Mobile sidebar
      showMobileSidebar: false,

      preloaderColor: 'rgba(14, 18, 23, 0.8)',

      // Destacado de la empresa
      mediaFailed: false,         // Falló src → se muestra el poster
      highlightFailed: false,     // Falló también el poster → se oculta el card
    };
  },
  computed: {
    // Obtener datos de la empresa actual desde la URL
    currentCompanyData() {
      const companySlug = this.$route.params.company;
      return topCompanies.find(c => c.slug === companySlug) || null;
    },

    // Destacado (video o imagen) de la empresa actual, si tiene
    highlight() {
      return this.currentCompanyData ? companyHighlights[this.currentCompanyData.slug] || null : null;
    },

    // Items filtrados por búsqueda
    filteredItems() {
      let filtered = this.loadedItems;

      // Filtro por texto
      const q = this.query.trim().toLowerCase();
      if (q) {
        filtered = filtered.filter(it =>
          (it.title && it.title.toLowerCase().includes(q)) ||
          (it.description && it.description.toLowerCase().includes(q))
        );
      }

      return filtered;
    },

    // Backward compatibility
    visible() {
      return this.filteredItems;
    }
  },
  methods: {
    onHighlightError() {
      if (!this.mediaFailed && this.highlight.poster) {
        this.mediaFailed = true;
      } else {
        this.highlightFailed = true;
      }
    },

    async loadMetadata() {
      try {
        const metadataUrl = `${feedChunksConfig.baseUrl}metadata.json`;
        const response = await fetch(metadataUrl);
        if (!response.ok) throw new Error('No se pudo cargar metadata');
        this.metadata = await response.json();
        console.log('📊 Metadata cargada para TOP Companies');
      } catch (e) {
        console.error('Error cargando metadata:', e);
        throw e;
      }
    },

    // Obtener el conteo de noticias por empresa
    getCompanyCount(category) {
      if (!this.metadata || !this.metadata.categories || !this.metadata.categories.notice) {
        return 0;
      }
      const categoryMeta = this.metadata.categories.notice.find(c => c.category === category);
      return categoryMeta ? categoryMeta.count : 0;
    },

    async loadChunk() {
      if (this.loadingMore || this.currentChunk >= this.totalChunks) {
        console.log(`⏸️ No se carga chunk: loadingMore=${this.loadingMore}, currentChunk=${this.currentChunk}, totalChunks=${this.totalChunks}`);
        return;
      }

      const isFirstLoad = this.currentChunk === 0;
      if (isFirstLoad) {
        this.initialLoading = true;
      } else {
        this.loadingMore = true;
      }

      this.error = '';

      try {
        let items = [];

        if (!this.currentCompanyData) {
          // Sin empresa válida, no cargar nada
          console.log('ℹ️ No hay empresa válida en la URL');
          this.error = 'Empresa no encontrada';
          return;
        }

        // Cargar chunk de la empresa actual
        const categoriesMetadata = this.metadata.categories.notice || [];
        const categoryMeta = categoriesMetadata.find(c => c.category === this.currentCompanyData.category);

        if (categoryMeta && categoryMeta.isPriority && categoryMeta.totalChunks > 0) {
          // Categoría prioritaria: tiene chunks propios
          if (this.currentChunk < categoryMeta.totalChunks) {
            const chunkFile = `${categoryMeta.chunkPrefix}-chunk-${this.currentChunk}.json`;
            const chunkUrl = `${feedChunksConfig.baseUrl}${chunkFile}`;
            const response = await fetch(chunkUrl);
            if (response.ok) {
              items = await response.json();
              console.log(`✅ Cargado: ${chunkFile} (${items.length} items)`);
            } else {
              console.warn(`⚠️ No se pudo cargar ${chunkFile}`);
              return;
            }
          } else {
            console.log(`ℹ️ No hay más chunks para ${this.selectedCompany} (${this.currentChunk}/${categoryMeta.totalChunks})`);
            return;
          }
        } else {
          // Categoría no prioritaria: filtrar del "all"
          const chunkFile = `notice-all-chunk-${this.currentChunk}.json`;
          const chunkUrl = `${feedChunksConfig.baseUrl}${chunkFile}`;
          const response = await fetch(chunkUrl);
          if (response.ok) {
            const allItems = await response.json();
            items = allItems.filter(item => item.sourceCategory === this.currentCompanyData.category);
            console.log(`✅ Cargado y filtrado: ${chunkFile} (${items.length}/${allItems.length} items de ${this.currentCompanyData.category})`);
          } else {
            console.warn(`⚠️ No se pudo cargar ${chunkFile}`);
            return;
          }
        }

        // Transformar items al formato esperado
        const transformedItems = items.map(item => {
          const uniqueId = item.id || item.link || `fallback-${item.title}-${item.pubDate}`;

          return {
            id: uniqueId,
            title: item.title || '',
            link: item.link || '',
            pubDate: item.pubDate ? new Date(item.pubDate) : null,
            description: item.description || '',
            category: item.sourceCategory || 'Sin categoría',
            sourceCategory: item.sourceCategory || 'Sin categoría',
            sourceType: item.sourceType || 'Otro',
            sourceTitle: item.sourceTitle || '',
            author: item.author || 'Desconocido',
            content: item.content || '',
            image: item.image || null,
            isShortVideo: item.isShortVideo || 0
          };
        });

        // Agregar nuevos items
        this.loadedItems.push(...transformedItems);

        // Remover duplicados
        const uniqueItemsMap = new Map();
        this.loadedItems.forEach(item => {
          if (!uniqueItemsMap.has(item.link)) {
            uniqueItemsMap.set(item.link, item);
          }
        });

        const beforeCount = this.loadedItems.length;
        this.loadedItems = Array.from(uniqueItemsMap.values());
        const afterCount = this.loadedItems.length;
        const duplicatesRemoved = beforeCount - afterCount;

        this.currentChunk++;

        console.log(`✅ Chunk ${this.currentChunk - 1} cargado (${items.length} items, ${duplicatesRemoved} duplicados removidos). Total único: ${this.loadedItems.length}`);

      } catch (e) {
        this.error = e.message || 'Error desconocido al cargar noticias';
        console.error('Error en loadChunk:', e);
      } finally {
        this.initialLoading = false;
        this.loadingMore = false;
      }
    },

    // Cargar empresa desde la URL
    async loadCompanyFromRoute() {
      if (!this.currentCompanyData) {
        this.error = 'Empresa no encontrada';
        console.error('❌ Empresa no válida en la URL');
        return;
      }

      console.log(`🔄 Cargando noticias de: ${this.currentCompanyData.name}`);

      this.mediaFailed = false;
      this.highlightFailed = false;

      // Limpiar items
      this.loadedItems = [];
      await this.$nextTick();

      // Actualizar totalChunks según la empresa
      const categoriesMetadata = this.metadata.categories.notice || [];
      const categoryMeta = categoriesMetadata.find(c => c.category === this.currentCompanyData.category);

      if (categoryMeta && categoryMeta.isPriority && categoryMeta.totalChunks > 0) {
        this.totalChunks = categoryMeta.totalChunks;
      } else {
        // Para categorías no prioritarias, usar los chunks del tipo completo
        this.totalChunks = this.metadata.byType.notice.totalChunks;
      }

      // Reset del chunk counter
      this.currentChunk = 0;

      // Scroll al inicio
      if (this.$refs.newsMain) {
        this.$refs.newsMain.scrollTop = 0;
      }

      // Cargar primer chunk
      await this.loadChunk();
    },

    handleScroll(event) {
      const element = event.target;
      const scrollTop = element.scrollTop;
      this.showScrollTop = scrollTop > 50;
    },

    scrollToTop() {
      if (this.$refs.newsMain) {
        this.$refs.newsMain.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      if (this.windowWidth >= 768) {
        this.showMobileSidebar = false;
      }
    },

    toggleMobileMenu() {
      this.showMobileSidebar = !this.showMobileSidebar;
    },

    handleSidebarCollapse(isCollapsed) {
      console.log('Sidebar collapsed:', isCollapsed);
    },

    async refresh() {
      console.log('🔄 Cargando metadata para TOP Companies...');

      // Reset estado
      this.loadedItems = [];
      this.currentChunk = 0;
      this.totalChunks = 0;
      this.error = '';
      this.initialLoading = true;

      try {
        // Cargar metadata
        await this.loadMetadata();

        // Cargar empresa desde la ruta
        await this.loadCompanyFromRoute();

      } catch (e) {
        this.error = e.message || 'Error al inicializar feeds';
        console.error('Error en refresh:', e);
      } finally {
        this.initialLoading = false;
      }
    },
  },

  async mounted() {
    await this.refresh();

    await this.$nextTick();

    // Agregar listener para resize
    window.addEventListener('resize', this.handleResize);

    // Asegurar que newsMain esté disponible
    setTimeout(() => {
      const newsMain = this.$refs.newsMain;
      if (newsMain) {
        newsMain.addEventListener('scroll', this.handleScroll, { passive: true });
      }
    }, 500);
  },

  // Watch para detectar cambios de empresa en la URL
  watch: {
    async '$route'(to, from) {
      // Si cambia el parámetro de empresa
      if (to.params.company !== from.params.company) {
        await this.loadCompanyFromRoute();
      }
    }
  },

  beforeUnmount() {
    const newsMain = this.$refs.newsMain;
    if (newsMain) {
      newsMain.removeEventListener('scroll', this.handleScroll);
    }
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
/* Dark Theme */
.news-app {
  min-height: 100vh;
  background: #0e1217;
  color: #e1e7ef;
  display: flex;
  flex-direction: column;
}

/* Main Layout */
.news-layout {
  display: flex;
  flex: 1;
  position: relative;
}

/* News Body */
.news-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 240px;
  height: calc(100vh - 57px);
  max-height: calc(100vh - 57px);
  background: #0e1217;
  transition: margin-left 0.3s ease;
  overflow: hidden;
}

.news-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 80px;
  height: 100%;
  max-height: calc(100vh - 57px);
}

.news-main__container {
  padding: 24px 20px 0;
  display: flex;
  flex-direction: column;
}

.news-content {
  padding-right: 8px;
}

/* Scrollbar */
.news-main::-webkit-scrollbar {
  width: 6px;
}
.news-main::-webkit-scrollbar-track {
  background: #1c2127;
  border-radius: 3px;
}
.news-main::-webkit-scrollbar-thumb {
  background: #2d333b;
  border-radius: 3px;
}
.news-main::-webkit-scrollbar-thumb:hover {
  background: #4a9eff;
}

/* Video destacado: card que ocupa 2 columnas del grid y toma el alto de la fila */
.company-hero {
  grid-column: span 2;
  display: flex;
  flex-direction: column;
  min-height: 280px;
  background: #1c2127;
  border: 1px solid #2d333b;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.company-hero__media {
  position: relative;
  flex: 1;
  background: #0e1217;
}

.company-hero__media video,
.company-hero__media img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.company-hero__caption {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 12px 16px;
  text-decoration: none;
  border-top: 1px solid #2d333b;
}

.company-hero__label {
  font-size: 12px;
  font-weight: 600;
  color: #4a9eff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.company-hero__title {
  font-size: 16px;
  font-weight: 700;
  color: #e1e7ef;
}

.company-hero__caption:hover .company-hero__title {
  color: #4a9eff;
}

/* Con el grid a 1 columna, span 2 crearía una columna extra */
@media (max-width: 1024px) {
  .company-hero {
    grid-column: 1 / -1;
    min-height: 0;
  }

  .company-hero__media {
    flex: none;
    aspect-ratio: 16 / 9;
  }
}

/* Volver a noticias */
.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #1c2127;
  border: 1px solid #2d333b;
  color: #a0aec0;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.back-btn:hover,
.back-btn:focus-visible {
  background: #262c34;
  color: #e1e7ef;
  transform: translateX(-2px);
}

/* Section Header */
.section-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 1px solid #2d333b;
}

.company-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  border: none;
}

.company-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.section-title-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.section-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: #e1e7ef;
  line-height: 1.2;
}

.section-description {
  font-size: 14px;
  color: #7d8694;
  margin: 0;
  font-weight: 500;
  line-height: 1.3;
}

/* Fila propia bajo avatar + nombre, alineada a la izquierda */
.company-about {
  flex-basis: 100%;
  margin: 0;
  max-width: 720px;
  font-size: 14px;
  line-height: 1.5;
  color: #a0aec0;
}

/* Error Message */
.error-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  margin-bottom: 20px;
}
.error-icon {
  color: #ef4444;
  flex-shrink: 0;
}
.error-content h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #fca5a5;
}
.error-content p {
  margin: 0;
  font-size: 14px;
  color: #fca5a5;
}

/* Loading Spinner */
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  color: #7d8694;
  font-size: 14px;
  font-weight: 600;
}
.loading-more .loading-spinner {
  border-color: rgba(74, 158, 255, 0.3);
  border-top-color: #4a9eff;
}

/* Load More Button */
.load-more-container {
  display: flex;
  justify-content: center;
  padding: 32px 20px;
}
.load-more-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #4a9eff, #7b68ee);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(74, 158, 255, 0.3);
}
.load-more-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(74, 158, 255, 0.4);
}
.load-more-btn:active {
  transform: translateY(0);
}
.load-more-btn svg {
  flex-shrink: 0;
  animation: bounce 2s infinite;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}

.end-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 20px;
  color: #34d399;
  font-size: 14px;
  font-weight: 600;
  background: rgba(52, 211, 153, 0.1);
  border: 1px solid rgba(52, 211, 153, 0.3);
  border-radius: 12px;
  margin-top: 20px;
}

/* Scroll to Top Button */
.scroll-to-top {
  position: fixed;
  bottom: 100px;
  right: 30px;
  width: 56px;
  height: 56px;
  background: white;
  color: black;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(74, 158, 255, 0.5);
  transition: all 0.3s ease;
  z-index: 300;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: scale(0.8);
}

.scroll-to-top--visible {
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
  transform: scale(1) !important;
}

.scroll-to-top--visible:hover {
  transform: scale(1.1) translateY(-3px) !important;
  box-shadow: 0 6px 20px rgba(74, 158, 255, 0.6);
}

.scroll-to-top--visible:active {
  transform: scale(0.95) !important;
}

.scroll-to-top svg {
  width: 24px;
  height: 24px;
  stroke-width: 2.5;
}

/* Mobile Overlay */
.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 400;
  backdrop-filter: blur(4px);
}

.sidebar--mobile-visible {
  left: 0 !important;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .mobile-overlay {
    display: block;
  }

  .news-body {
    margin-left: 0;
  }

  .news-main {
    padding-bottom: 60px;
  }

  .section-header {
    gap: 16px;
    padding-bottom: 16px;
  }

  .company-avatar {
    width: 56px;
    height: 56px;
    border-width: 2px;
  }

  .section-title {
    font-size: 22px;
  }

  .section-description {
    font-size: 13px;
  }

  .scroll-to-top {
    bottom: 70px;
    right: 16px;
    width: 44px;
    height: 44px;
  }

  .scroll-to-top svg {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .news-main__container {
    padding: 16px 12px 0;
  }

  .section-header {
    gap: 12px;
    margin-bottom: 20px;
    padding-bottom: 16px;
  }

  .company-avatar {
    width: 48px;
    height: 48px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-description {
    font-size: 12px;
  }

  .company-about {
    font-size: 13px;
  }
}
</style>
